import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, VideoCamera, Note, Heartbeat, CheckSquare, ChartLine, UsersFour, Barbell,
  Warning, UsersThree,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const CARDS = [
  { to: "/coach/clients",     label: "Athletes",            icon: UsersFour,     desc: "Roster & readiness" },
  { to: "/coach/teams",       label: "Teams",               icon: UsersFour,     desc: "Squad management" },
  { to: "/programs",          label: "Skill Sessions",      icon: Barbell,       desc: "Technical drills & sport-specific work" },
  { to: "/reviews",           label: "Technical Videos",    icon: VideoCamera,   desc: "Upload / review technique" },
  { to: "/coach/clients",     label: "Technical Notes",     icon: Note,          desc: "Coach cues & feedback" },
  { to: "/coach/performance", label: "Assessments",         icon: ChartLine,     desc: "Read-only performance data" },
  { to: "/coach/clients",     label: "Attendance",          icon: CheckSquare,   desc: "Session check-ins" },
  { to: "/coach/clients",     label: "Readiness",           icon: Heartbeat,     desc: "Daily athlete readiness" },
  { to: "/dashboard",         label: "Competitions",        icon: Trophy,        desc: "Fixtures & results" },
];

const kpiCard = (label, value, Icon, testid) => (
  <div className="bg-surface-DEFAULT p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <Icon size={14} weight="duotone" className="text-cyan-300" />
      <span className="label-tiny text-[9px] text-text-secondary">{label}</span>
    </div>
    <div className="font-heading text-2xl text-cyan-300 tabular-nums">{value ?? "—"}</div>
  </div>
);

export default function SportsCoachDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/sports-coach/dashboard").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="sports-coach-dashboard">
      <div className="mb-8">
        <div className="label-tiny text-cyan-300 mb-2">// SPORTS COACH MODE</div>
        <h1 className="font-heading text-4xl sm:text-5xl">TECHNICAL HUB</h1>
        <p className="text-text-secondary text-sm mt-2">
          {user?.name?.split(" ")[0] || "Coach"} · Technical coaching + read-only S&amp;C context.
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mb-6" data-testid="sc-kpis">
          {kpiCard("ATHLETES",   data.roster_count,          UsersFour,  "sc-kpi-athletes")}
          {kpiCard("TEAMS",      data.teams_count,           UsersThree, "sc-kpi-teams")}
          {kpiCard("ALERTS",     data.readiness_alert_count, Warning,    "sc-kpi-alerts")}
          {kpiCard("RECENT TESTS", data.recent_tests?.length ?? 0, ChartLine, "sc-kpi-tests")}
        </div>
      )}

      {/* Live signal */}
      {data && (data.low_readiness?.length > 0 || data.upcoming_competitions?.length > 0 || data.recent_tests?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border border-slate-200 p-4" data-testid="sc-readiness-list">
            <div className="label-tiny mb-3">// READINESS ALERTS ({data.readiness_alert_count})</div>
            {data.low_readiness.length === 0 ? (
              <div className="text-xs text-text-secondary italic">Roster is clear.</div>
            ) : (
              <div className="space-y-1.5">
                {data.low_readiness.map((r) => (
                  <Link key={r.client_id} to={`/coach/clients/${r.client_id}`} className="flex items-center justify-between gap-2 hover:bg-slate-100 px-2 py-1.5">
                    <div className="text-sm font-heading truncate">{r.client_name}</div>
                    <div className={`font-heading tabular-nums ${r.state === "low" ? "text-red-500" : "text-brand-accent"}`}>{r.score ?? "—"}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="border border-slate-200 p-4" data-testid="sc-competitions-list">
            <div className="label-tiny mb-3">// UPCOMING COMPETITIONS ({data.upcoming_competitions.length})</div>
            {data.upcoming_competitions.length === 0 ? (
              <div className="text-xs text-text-secondary italic">Nothing on the horizon.</div>
            ) : (
              <div className="space-y-1.5 text-xs">
                {data.upcoming_competitions.map((c) => {
                  const pstyle =
                    c.priority === "A" ? "bg-red-500 text-white"          :
                    c.priority === "B" ? "bg-amber-400 text-surface-base" :
                    c.priority === "C" ? "bg-white/20 text-slate-800"      : null;
                  return (
                  <div key={c.competition_id} className="flex items-center justify-between gap-2">
                    <div className="truncate flex items-center gap-1.5 min-w-0">
                      {pstyle && <span className={`label-tiny text-[8px] px-1 py-0.5 shrink-0 ${pstyle}`}>{c.priority}</span>}
                      <span className="text-white truncate">{c.client_name}</span>
                      <span className="text-text-secondary truncate"> · {c.name}</span>
                    </div>
                    <span className="text-cyan-300 tabular-nums shrink-0">{new Date(c.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {data && data.recent_tests?.length > 0 && (
        <div className="border border-slate-200 p-4 mb-6" data-testid="sc-tests-list">
          <div className="label-tiny mb-3">// RECENT TESTS ({data.recent_tests.length})</div>
          <div className="space-y-1 text-xs">
            {data.recent_tests.map((t) => (
              <div key={t.test_id} className="flex items-center justify-between gap-3">
                <div className="truncate">
                  <span className="text-white">{t.client_name}</span>
                  <span className="text-text-secondary"> · {t.test_name}</span>
                </div>
                <span className="text-cyan-300 tabular-nums">{t.best_score} {t.unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10" data-testid="sc-quickactions">
        {CARDS.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            data-testid={`sc-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="bg-surface-DEFAULT hover:bg-surface-elevated p-5 group transition"
          >
            <c.icon size={20} weight="duotone" className="text-cyan-300 mb-3" />
            <div className="font-heading text-lg mb-1">{c.label.toUpperCase()}</div>
            <div className="text-xs text-text-secondary">{c.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
